import type { MidiNote, MidiTimeSignature, MidiTimingMetadata } from "./music";

export type ImportedMidiTrack = {
  index: number;
  name: string;
  notes: MidiNote[];
};

export type ImportedMidiFile = {
  format: number;
  bpm: number;
  timing: Required<Pick<MidiTimingMetadata, "ppq">> & { timeSignature: MidiTimeSignature };
  tracks: ImportedMidiTrack[];
};

class MidiReader {
  readonly bytes: Uint8Array;
  offset: number;

  constructor(bytes: Uint8Array, offset = 0) {
    this.bytes = bytes;
    this.offset = offset;
  }

  remaining(): number {
    return this.bytes.length - this.offset;
  }

  u8(): number {
    if (this.offset >= this.bytes.length) throw new Error("MIDIファイルが途中で終わっています");
    return this.bytes[this.offset++];
  }

  u16(): number {
    return (this.u8() << 8) | this.u8();
  }

  u32(): number {
    return ((this.u8() << 24) >>> 0) + (this.u8() << 16) + (this.u8() << 8) + this.u8();
  }

  text(length: number): string {
    return new TextDecoder().decode(this.take(length));
  }

  take(length: number): Uint8Array {
    if (length < 0 || this.offset + length > this.bytes.length) throw new Error("MIDI chunkの長さが不正です");
    const result = this.bytes.subarray(this.offset, this.offset + length);
    this.offset += length;
    return result;
  }

  variableLength(): number {
    let value = 0;
    for (let index = 0; index < 4; index += 1) {
      const byte = this.u8();
      value = (value << 7) | (byte & 0x7f);
      if ((byte & 0x80) === 0) return value;
    }
    throw new Error("MIDIの可変長値が不正です");
  }
}

function expectChunk(reader: MidiReader, expected: string): number {
  const name = reader.text(4);
  if (name !== expected) throw new Error(expected + " chunkが見つかりません");
  return reader.u32();
}

function closeActiveNote(
  active: Map<string, Array<{ tick: number; velocity: number }>>,
  notes: MidiNote[],
  channel: number,
  midi: number,
  endTick: number,
  ppq: number,
) {
  const key = channel + ":" + midi;
  const starts = active.get(key);
  const started = starts?.shift();
  if (!started) return;
  if (!starts?.length) active.delete(key);
  notes.push({
    midi,
    startBeat: started.tick / ppq,
    durationBeats: Math.max(1 / ppq, (endTick - started.tick) / ppq),
    velocity: started.velocity,
  });
}

export function parseMidiFile(bytes: Uint8Array): ImportedMidiFile {
  const reader = new MidiReader(bytes);
  const headerLength = expectChunk(reader, "MThd");
  if (headerLength < 6) throw new Error("MIDI headerが短すぎます");
  const format = reader.u16();
  const declaredTrackCount = reader.u16();
  const division = reader.u16();
  if ((division & 0x8000) !== 0) throw new Error("SMPTE time divisionのMIDIは現在編集できません");
  const ppq = division;
  if (ppq <= 0) throw new Error("PPQが不正です");
  if (headerLength > 6) reader.take(headerLength - 6);

  let bpm = 120;
  let tempoFound = false;
  let timeSignature: MidiTimeSignature = { numerator: 4, denominator: 4 };
  let signatureFound = false;
  const tracks: ImportedMidiTrack[] = [];

  for (let trackIndex = 0; trackIndex < declaredTrackCount && reader.remaining() >= 8; trackIndex += 1) {
    const trackLength = expectChunk(reader, "MTrk");
    const trackReader = new MidiReader(reader.take(trackLength));
    let tick = 0;
    let runningStatus = 0;
    let trackName = "";
    const notes: MidiNote[] = [];
    const active = new Map<string, Array<{ tick: number; velocity: number }>>();

    while (trackReader.remaining() > 0) {
      tick += trackReader.variableLength();
      let status = trackReader.u8();
      let firstData: number | null = null;
      if (status < 0x80) {
        if (runningStatus < 0x80) throw new Error("running statusが不正です");
        firstData = status;
        status = runningStatus;
      } else if (status < 0xf0) {
        runningStatus = status;
      }

      if (status === 0xff) {
        const metaType = trackReader.u8();
        const length = trackReader.variableLength();
        const data = trackReader.take(length);
        if (metaType === 0x03 && !trackName) trackName = new TextDecoder().decode(data).trim();
        if (metaType === 0x51 && data.length === 3 && !tempoFound) {
          const microseconds = (data[0] << 16) | (data[1] << 8) | data[2];
          if (microseconds > 0) {
            bpm = Math.max(1, Math.min(999, Math.round(60_000_000 / microseconds * 100) / 100));
            tempoFound = true;
          }
        }
        if (metaType === 0x58 && data.length >= 2 && !signatureFound) {
          timeSignature = {
            numerator: Math.max(1, data[0]),
            denominator: 2 ** Math.min(7, data[1]),
          };
          signatureFound = true;
        }
        if (metaType === 0x2f) break;
        continue;
      }

      if (status === 0xf0 || status === 0xf7) {
        trackReader.take(trackReader.variableLength());
        runningStatus = 0;
        continue;
      }

      const command = status & 0xf0;
      const channel = status & 0x0f;
      const dataLength = command === 0xc0 || command === 0xd0 ? 1 : 2;
      const data1 = firstData ?? trackReader.u8();
      const data2 = dataLength === 2 ? trackReader.u8() : 0;
      if (command === 0x90 && data2 > 0) {
        const key = channel + ":" + data1;
        const starts = active.get(key) ?? [];
        starts.push({ tick, velocity: Math.max(1, Math.min(127, data2)) });
        active.set(key, starts);
      } else if (command === 0x80 || (command === 0x90 && data2 === 0)) {
        closeActiveNote(active, notes, channel, data1, tick, ppq);
      }
    }

    tracks.push({
      index: trackIndex,
      name: trackName || "Track " + (trackIndex + 1),
      notes: notes.sort((left, right) => left.startBeat - right.startBeat || left.midi - right.midi),
    });
  }

  const editableTracks = tracks.filter((track) => track.notes.length > 0);
  if (!editableTracks.length) throw new Error("編集できるNote On/Offが見つかりません");
  return {
    format,
    bpm,
    timing: { ppq, timeSignature },
    tracks: editableTracks,
  };
}